import type { IncidentV3 } from '@aithora/shared';
import { durNoSec } from './lib';

const KIND: Record<string, string> = { deploy: 'Deploy', flag: 'Config', model: 'Model', vendor: 'Vendor' };

/**
 * What an incident adds to the drawer and the Incidents tab (design pfIncExtra, L18785):
 * who it reaches, what changed in the hour before, the runbook, and closing the loop.
 * Everything is read from GET /pulse-v3/incidents; a missing incident reads as the
 * design's "nothing linked" lines.
 */
export function incidentExtra(inc: IncidentV3 | null, now: number) {
  if (!inc) return null;
  const changes = inc.changes ?? [];
  return {
    blast: inc.blast_radius?.note ?? (inc.blast_radius ? blastWords(inc) : inc.blast_radius_reason ?? ''),
    changed: changes.map((c) => ({
      when: '−' + durNoSec(c.at ? (now - Date.parse(c.at)) / 60000 : 0),
      kind: KIND[c.kind] ?? c.kind,
      what: c.ref,
      who: c.by ?? 'not recorded',
    })),
    changedNote: changes.length
      ? 'Only Aithora-side changes are visible. Facility-side changes are not reported to us yet.'
      : inc.scope === 'facility'
        ? 'Nothing changed on the Aithora side in the hour before. Facility-side changes (network, tablets) are not visible to us yet.'
        : 'No change recorded in the hour before.',
    rbName: inc.runbook ? inc.runbook.id + ' · ' + inc.runbook.name : inc.runbook_id ?? 'none linked',
    rbSteps: (inc.runbook?.steps ?? []).map((t, i) => ({ n: String(i + 1), t })),
    rbMeta: inc.runbook?.owner_note ?? (inc.runbook_id ? '' : 'Every alert needs a runbook before it can page.'),
    review: inc.review.note ?? (inc.review.done ? 'Review done.' : 'Review due within 5 working days of resolve.'),
    facNote: inc.facility_note ?? '',
  };
}

function blastWords(inc: IncidentV3): string {
  const b = inc.blast_radius;
  if (!b) return '';
  const who = connectedBuildings(b.live_buildings) + (b.residents == null ? '' : ', ' + b.residents + (b.residents === 1 ? ' resident' : ' residents'));
  return (b.capabilities?.length ? b.capabilities.join(', ') + ' at ' : '') + who + '.';
}

/**
 * V9 wording: the buildings on the live runtime are "connected" (the other pilot buildings run
 * their pilot but are not on it yet), never "live" ("3 connected buildings").
 */
export function connectedBuildings(n: number): string {
  return n + (n === 1 ? ' connected building' : ' connected buildings');
}

/**
 * What a service carries, in V9 words: the server's "carries Atlas replies · 3 buildings"
 * reads "carries Atlas replies · 3 connected buildings".
 */
export function carriesWords(s: string | null | undefined): string {
  // Only the server's own "carries …" line (station-api.ts) is reworded; anything else passes as sent.
  return (s ?? '').replace(/^(carries .+) · (\d+) buildings?$/, (_m, head: string, n: string) => head + ' · ' + connectedBuildings(Number(n)));
}
